import { useEffect } from 'react';

/**
 * Centralized <head> manager: sets the page title, description, Open Graph /
 * Twitter Card tags, canonical URL, and (optionally) JSON-LD structured data.
 *
 * Pass `jsonLd` as an object or an array of objects — each will be rendered
 * as its own <script type="application/ld+json"> tag and cleaned up on
 * unmount so tags don't accumulate across route changes.
 */
const DEFAULT_TITLE = 'RoomHora — Find your place, not just a listing';
const DEFAULT_DESCRIPTION =
  'RoomHora is a map-first rental marketplace with verified owners, transparent pricing, and direct messaging. Search rooms, apartments, houses, and studios worldwide.';
const SITE_NAME = 'RoomHora';
const TWITTER_HANDLE = '@roomhora';

const SEOHead = ({
  title,
  description,
  image,
  url,
  type = 'website',
  jsonLd,
  noIndex = false,
}) => {
  const finalTitle = title
    ? title.includes('RoomHora')
      ? title
      : `${title} · RoomHora`
    : DEFAULT_TITLE;
  const finalDescription = description || DEFAULT_DESCRIPTION;
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://room-hora.vercel.app';
  const finalImage = image || `${origin}/og-image.png`;
  const finalUrl =
    url || (typeof window !== 'undefined' ? window.location.href : origin);

  useEffect(() => {
    document.title = finalTitle;

    const setMeta = (key, content, isProperty = false) => {
      if (content == null) return;
      const selector = isProperty
        ? `meta[property="${key}"]`
        : `meta[name="${key}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (isProperty) el.setAttribute('property', key);
        else el.setAttribute('name', key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', finalDescription);
    setMeta('viewport', 'width=device-width, initial-scale=1, maximum-scale=5');
    setMeta('theme-color', '#6366f1');
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    setMeta('og:title', finalTitle, true);
    setMeta('og:description', finalDescription, true);
    setMeta('og:image', finalImage, true);
    setMeta('og:image:alt', `${SITE_NAME} — ${finalTitle}`, true);
    setMeta('og:url', finalUrl, true);
    setMeta('og:type', type, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:locale', 'en_US', true);

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', TWITTER_HANDLE);
    setMeta('twitter:creator', TWITTER_HANDLE);
    setMeta('twitter:title', finalTitle);
    setMeta('twitter:description', finalDescription);
    setMeta('twitter:image', finalImage);
    setMeta('twitter:image:alt', `${SITE_NAME} — ${finalTitle}`);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', finalUrl);

    // JSON-LD structured data — append one script per block, tagged with a
    // data-attribute so we can tear them down on unmount.
    const blocks = Array.isArray(jsonLd)
      ? jsonLd.filter(Boolean)
      : jsonLd
        ? [jsonLd]
        : [];
    const injected = [];
    blocks.forEach((block, i) => {
      try {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-ld', `${i}`);
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
        injected.push(script);
      } catch {
        // swallow — bad JSON should never break rendering
      }
    });

    return () => {
      injected.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
    };
  }, [finalTitle, finalDescription, finalImage, finalUrl, type, noIndex, jsonLd]);

  return null;
};

/** ---------- JSON-LD helpers ---------- */

/** Organization block — use once on the landing page. */
export function orgJsonLd({ origin = 'https://room-hora.vercel.app' } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'RoomHora',
    url: origin,
    logo: `${origin}/roomhora-icon-1024.png`,
    sameAs: [
      'https://www.instagram.com/roomhoraofficial',
      'https://x.com/roomhora',
      'https://www.tiktok.com/@roomhora',
    ],
  };
}

/** WebSite + SearchAction block — enables Google's "sitelinks searchbox". */
export function websiteJsonLd({ origin = 'https://room-hora.vercel.app' } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'RoomHora',
    url: origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${origin}/search?query={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Property listing → schema.org/Apartment (which is a kind of Residence).
 * Covers the important bits search engines look for: price, address, geo,
 * images, bed/bath count. Kept minimal — only fields we're confident about.
 */
export function listingJsonLd(property, { origin = 'https://room-hora.vercel.app' } = {}) {
  if (!property) return null;
  const url = `${origin}/property/${property.property_id}`;
  const address = property.location || {};
  const images = (property.media || [])
    .map((m) => m?.url)
    .filter(Boolean)
    .slice(0, 6);

  const base = {
    '@context': 'https://schema.org',
    '@type': 'Apartment',
    name: property.title,
    description: property.description,
    url,
    image: images.length ? images : undefined,
    numberOfBedrooms: property.details?.bedroom_count || undefined,
    numberOfBathroomsTotal: property.details?.bathroom_count || undefined,
    floorSize:
      property.details?.area_sqft > 0
        ? {
            '@type': 'QuantitativeValue',
            value: property.details.area_sqft,
            unitCode: 'FTK',
          }
        : undefined,
    address: {
      '@type': 'PostalAddress',
      addressLocality: address.city || undefined,
      addressRegion: address.state || undefined,
      addressCountry: address.country || undefined,
      streetAddress: address.street || address.area || undefined,
    },
    geo:
      address.latitude && address.longitude
        ? {
            '@type': 'GeoCoordinates',
            latitude: address.latitude,
            longitude: address.longitude,
          }
        : undefined,
    offers: property.monthly_rent
      ? {
          '@type': 'Offer',
          price: property.monthly_rent,
          priceCurrency: property.currency || 'USD',
          availability: 'https://schema.org/InStock',
          url,
        }
      : undefined,
  };

  if (property.rating_average && property.rating_count) {
    base.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(property.rating_average).toFixed(1),
      reviewCount: property.rating_count,
      bestRating: 5,
      worstRating: 1,
    };
  }

  // Strip any undefined keys so the JSON is clean.
  return JSON.parse(JSON.stringify(base));
}

/** BreadcrumbList helper — pass an array of {name, url} in order. */
export function breadcrumbJsonLd(items = []) {
  if (!items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export default SEOHead;
